/**
 * An email, shown the way a mail client would show it.
 *
 * A message is a whole document with its own styling, and this application is
 * a whole document with its own styling, and the two do not belong in the same
 * one. Dropped straight into the page, a campaign built the way campaigns are
 * built — nested tables, `bgcolor`, fixed widths — picks up every table rule
 * the app has. `tbody tr:hover td{background:var(--hover)}` repainted a button
 * the moment the pointer crossed it, so the white text vanished; the hover
 * marker drew a bar down the side of it; and the padding on `td` pushed a
 * 560px layout past the edge of the panel.
 *
 * So the message goes in a frame of its own, which is what a mail client gives
 * it. Nothing here leaks in, and nothing in the message reaches out.
 *
 * Scripts are off. The frame keeps the origin — otherwise its height could not
 * be measured, and the pictures, which are object URLs this tab made, would
 * not load — and keeping the origin is only safe because nothing inside can
 * run.
 */
import { useEffect, useRef, useState } from 'react';

/*
 * Deliberately thin. Mail clients apply a margin, a default font and little
 * else, and every rule added here is a way for the preview to disagree with
 * what actually lands in somebody's inbox.
 */
const SHELL = (body, extra) => `<!doctype html>
<html><head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width,initial-scale=1">
<base target="_blank">
<style>
  html,body{margin:0;padding:0;background:#ffffff;color:#1f2430;
    font:14px/1.55 -apple-system,BlinkMacSystemFont,"Segoe UI",Arial,sans-serif;
    -webkit-text-size-adjust:100%}
  img{max-width:100%;height:auto;border:0}
${extra || ''}
</style>
</head><body>${body}</body></html>`;

/*
 * `extraCss` is for marking the message up rather than restyling it — the
 * click map hangs a badge on every link that earned one. It goes inside the
 * frame because that is the only place a rule can reach the message at all.
 */
export default function MailPreview({ html, minHeight = 120, extraCss = '' }) {
  const frame = useRef(null);
  const [height, setHeight] = useState(minHeight);

  useEffect(() => {
    const el = frame.current;
    if (!el) return undefined;

    let watcher = null;

    /* An iframe has no height of its own, so without measuring, every message
       is either cut off or sits in a tall box of empty white. */
    const fit = () => {
      const doc = el.contentDocument;
      if (!doc || !doc.documentElement) return;
      setHeight(Math.max(doc.documentElement.scrollHeight, minHeight));
    };

    const attach = () => {
      if (watcher) watcher.disconnect();
      fit();

      const doc = el.contentDocument;
      if (!doc || typeof ResizeObserver === 'undefined') return;

      // Pictures arrive after the document does, and each one moves the rest
      // of the message down.
      watcher = new ResizeObserver(fit);
      watcher.observe(doc.documentElement);
    };

    el.addEventListener('load', attach);
    // Already there: a re-render with the same markup fires no second load.
    if (el.contentDocument && el.contentDocument.readyState === 'complete') attach();

    return () => {
      el.removeEventListener('load', attach);
      if (watcher) watcher.disconnect();
    };
  }, [html, minHeight, extraCss]);

  return (
    <iframe
      ref={frame}
      className="mailframe"
      title="Message preview"
      srcDoc={SHELL(html || '', extraCss)}
      sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
      style={{ height }}
    />
  );
}
